'use client';

import type { ProductFilterSchema } from '@coas/validators/payload';
import { useSearchParams } from 'next/navigation';
import type { UseFormReturn } from 'react-hook-form';
import { api } from 'trpc/react';
import type { RouterOutputs } from 'trpc/shared';
import IconChecked from '~/shared/assets/icons/checked.svg';
import { Spinner } from '~/shared/ui/spinner';

type FilterSizeProps = {
    category: RouterOutputs['category']['getBySlug'] | null;
    list?: RouterOutputs['list']['getBySlug'] | null;
    form: UseFormReturn<ProductFilterSchema>;
};

type SizeRowGroup = RouterOutputs['size']['getRowFilters'][number];

export default ({ category, list, form }: FilterSizeProps) => {
    const searchParams = useSearchParams();

    const { register, watch } = form;
    // sizes нам не нужны для построения фильтра — убираем из запроса,
    // чтобы не фильтровать самим же по выбранным размерам
    const { sizes: _sizes, ...filter } = watch();

    const {
        data: groups,
        isLoading,
        isSuccess,
    } = api.size.getRowFilters.useQuery(
        {
            category: category?.item.id ?? null,
            list: list?.item.id ?? null,
            search: searchParams.get('search') ?? '',
            filter,
        },
        {
            refetchOnWindowFocus: false,
        },
    );

    return (
        <>
            {isLoading && <Spinner className='absolute inset-0 m-auto h-8 w-8' />}
            <div className='flex w-full flex-col'>
                {isSuccess &&
                    groups?.map((group: SizeRowGroup) => (
                        <div key={group.chartId} className='w-full border-b border-neutral-200 last:border-b-0'>
                            <div className='px-page pt-3 pb-1'>
                                <div className='text-16 font-medium text-neutral-950'>{group.chartName}</div>
                            </div>

                            {group.rows.map(row => (
                                <label
                                    key={row.id}
                                    htmlFor={row.id}
                                    className='flex w-full items-center gap-x-4 py-2.5'
                                >
                                    <input
                                        id={row.id}
                                        type='checkbox'
                                        value={row.id}
                                        className='peer -z-50 absolute opacity-0'
                                        {...register('sizes')}
                                    />
                                    <span className='flex-1 space-x-3 px-page text-start'>
                                        <span className='text-18 text-neutral-950'>{row.value}</span>
                                        <span className='font-300 text-16 text-neutral-500'>({row.count})</span>
                                    </span>
                                    <IconChecked className='mb-1 h-5 w-5 stroke-transparent peer-checked:stroke-neutral-950' />
                                </label>
                            ))}
                        </div>
                    ))}
            </div>
        </>
    );
};
